import {
  getGatewayApiUrl,
  getGatewayNavigationUrl,
  isAbsoluteUrl,
  isPublicEditorPath,
} from "./backendUrl";
import { csrfHeaders } from "./csrf";
import { normalizeApiError } from "./problem";

const baseFetchConfig: RequestInit = { credentials: "include" };

async function handleResponse<TResponse>(response: Response): Promise<TResponse> {
  if (!response.ok) {
    const error = await normalizeApiError(response);
    if (error.status === 401 && !isPublicEditorPath(window.location.pathname)) {
      window.location.href = getGatewayNavigationUrl("login");
    }
    throw error;
  }
  if (response.status === 204) return undefined as TResponse;
  return response.json() as Promise<TResponse>;
}

async function request<TResponse>(url: string, init: RequestInit = {}): Promise<TResponse> {
  const method = (init.method ?? "GET").toUpperCase();
  const mutating = !["GET", "HEAD", "OPTIONS"].includes(method);
  const csrf = mutating && !isAbsoluteUrl(url) ? await csrfHeaders() : {};

  const response = await fetch(getGatewayApiUrl(url), {
    ...baseFetchConfig,
    ...init,
    headers: { ...init.headers, ...csrf },
  });
  return handleResponse<TResponse>(response);
}

const api = {
  get: <TResponse>(url: string): Promise<TResponse> => request<TResponse>(url),
  post: <TRequest = undefined, TResponse = void>(
    url: string,
    data?: TRequest
  ): Promise<TResponse> =>
    request<TResponse>(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: data === undefined ? undefined : JSON.stringify(data),
    }),
  put: <TRequest = undefined, TResponse = void>(url: string, data?: TRequest): Promise<TResponse> =>
    request<TResponse>(url, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: data === undefined ? undefined : JSON.stringify(data),
    }),
  delete: <TResponse = void>(url: string): Promise<TResponse> =>
    request<TResponse>(url, { method: "DELETE" }),
};

export default api;
